import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Filter, Loader, PencilIcon, PlusIcon, SendIcon, UserIcon, UsersRoundIcon } from "lucide-react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { ChatType, fetchChatsPage, useChatsPaginated, ChatStream, ChatsParams, ChatTypeOptions, ChatSourceOptions, MessageGroupOptions, useSendTestMessage, TwilioMessageName, SendTestMessageValidation, useChatMessagesPaginated, fetchChatMessagesPage } from "@/lib/api/chats";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatList } from "./ChatList";
import { ChatDetail } from "./ChatDetail";
import { CreateChatStream, UpdateChatStream } from "./ManageChatStream";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { BearInput, BearSelectInput } from "@/lib/form/ui";
import { useQueryParam } from "@/hooks/use-query-param";
import { findByKey } from "@/lib/prelude";
import { convertStreamTypeToChatType } from "./utils";
import { ExtendChatStream } from "./ManageChatStream/ExtendChatGroup";
import { GroupChatParticipantsSidebar } from "./GroupChatParticipantsSidebar";

export function GroupChatSection() {
  const pageSize = 50;
  const [page, setPage] = useState(1);
  const [temporaryChatParams, setTemporaryChatParams] = useState<Omit<ChatsParams, "page">>({});
  const [chatParams, setChatParams] = useState<Omit<ChatsParams, "page">>({});
  const [selectedChatStreamId, setSelectedChatStreamId] = useQueryParam('selectedChatStream');
  const [extendChatStream, setExtendChatStream] = useState<ChatStream | null>(null);
  const [updateChatStream, setUpdateChatStream] = useState<ChatStream | null>(null);
  const chatsRes: any = useChatsPaginated({
    params: { ...chatParams, page, chatType: convertStreamTypeToChatType('group'), pageSize },
    queryFn: () => fetchChatsPage({ ...chatParams, page, chatType: convertStreamTypeToChatType('group'), pageSize })
  });

  const chatData = chatsRes?.data?.data || {};
  const chatItems = chatData.data || [];
  const pagination = chatData.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize
  };

  const selectedChat = selectedChatStreamId
    ? chatItems.find((chat: ChatStream) => chat.id === selectedChatStreamId)
    : null;

  const allParticipants = selectedChat?.Participants || [];

  const columns: DataTableColumn<ChatStream>[] = [
    {
      id: "actions",
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => {
        return (
          <span className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 mr-2"
              title="New Group From"
              disabled={!!info.row.original.system_chat_type}
              onClick={() => setExtendChatStream(info.row.original)}
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 mr-2"
              title="Edit Group"
              disabled={!!info.row.original.system_chat_type}
              onClick={() => setUpdateChatStream(info.row.original)}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          </span>
        );
      },
      size: 70,
      maxSize: 70,
      disableRowClick: true,
      enableFiltering: false,
      enableSorting: false,
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => {
        return moment.tz(info.getValue(), CommonDestinationTimezone).format('YYYY-MM-DD hh:mm a');
      }
    },
    {
      id: "message_service_env",
      accessorKey: "message_service_env",
      header: "Feature",
      accessorFn: (row) => {
        const base_message_group_env = row.message_service_env;
        return findByKey("value", base_message_group_env)(MessageGroupOptions)?.label || base_message_group_env;
      }
    },
  ];

  return (
    <div className={cn("w-full h-full flex flex-col")}>
      {!selectedChat ? (
        <DataTable
          tableId="GroupChatBaseList"
          defaultSorting={[{ id: "created_at", desc: true }]}
          columns={columns as any}
          data={chatItems}
          stickyHeaders
          enableColumnVisibility
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnResizing
          enableColumnPinning
          filterStyle='select'
          filterMode='internal'
          tableClassName={"h-[calc(100vh-16rem)] overflow-y-auto"}
          emptyMessage={chatsRes.isFetching ? "Loading..." : "No group chats found"}
          rowClickable={true}
          onRowClick={(row) => setSelectedChatStreamId(row.id)}
        />
      ) : (
        <div className="flex flex-col flex-grow h-[calc(100%-3rem)] md:grid md:grid-cols-[minmax(300px,400px),1fr] gap-4">
          <div className={cn("w-full h-full border rounded-lg overflow-hidden flex flex-col", "hidden md:block")}>
            <GroupChatParticipantsSidebar participants={allParticipants} />
          </div>
          <div className={cn("w-full h-full border rounded-lg overflow-hidden flex flex-col")}>
            <ChatDetail
              chat={selectedChat}
              onBackClick={() => setSelectedChatStreamId(null)}
            />
          </div>
        </div>
      )}

      {extendChatStream && (
        <ExtendChatStream
          chatStream={extendChatStream}
          onSuccess={() => {
            setExtendChatStream(null);
          }}
          onClose={() => setExtendChatStream(null)}
        />
      )}

      {updateChatStream && (
        <UpdateChatStream
          chatStream={updateChatStream}
          onSuccess={() => {
            setUpdateChatStream(null);
          }}
          onClose={() => setUpdateChatStream(null)}
        />
      )}
    </div>
  );
}
